// Collection menu
// - Sticky collection nav: smooth-scroll to a group, scroll-spy highlight,
//   horizontal overflow arrow.
// - Client-side filtering by the products' `preferences_alimentaires` and
//   `proteines` metaobjects (rendered into data attributes). Dietary
//   preferences are AND'd (constraints: a card must carry EVERY selected
//   preference); proteins are OR'd (choices: any selected protein matches);
//   across the two groups selections are AND'd. Collections with no matching
//   products are hidden (along with their nav item). No network requests —
//   all matching happens against cached handle arrays in memory.
// - "Voir plus" reveals the cards held back by the per-collection initial limit.
// - Variant chips are in-card selectors (no navigation): clicking swaps the card
//   image to the variant's image; re-clicking the active chip restores the
//   product image. Only the media/title links navigate (UEAT order URL).
class CollectionMenu extends HTMLElement {
  connectedCallback() {
    this.nav = this.querySelector('[data-cm-nav]');
    this.navScroll = this.querySelector('[data-cm-nav-scroll]');
    this.navNext = this.querySelector('[data-cm-nav-next]');
    this.navPrev = this.querySelector('[data-cm-nav-prev]');
    this.links = Array.from(this.querySelectorAll('[data-cm-nav-link]'));
    this.groups = Array.from(this.querySelectorAll('[data-cm-group]'));

    this.linkById = new Map(this.links.map((l) => [l.dataset.cmNavLink, l]));
    this.selected = {
      'preferences-alimentaires': new Set(),
      proteines: new Set(),
    };
    this.results = this.querySelector('[data-cm-results]');
    this.activeBar = this.querySelector('[data-cm-active]');
    this.chipsList = this.querySelector('[data-cm-chips]');
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.mq = window.matchMedia('(max-width: 749px)');
    // Re-render when crossing the mobile breakpoint (the per-collection limit changes).
    this._onMq = () => {
      this.applyFilters(false);
      this.updateScrollOffset();
    };
    this.mq.addEventListener('change', this._onMq);

    // Map "type:handle" -> display label, for building the chips.
    this.labelMap = new Map();
    this.querySelectorAll('[data-cm-filter]').forEach((btn) => {
      this.labelMap.set(btn.dataset.cmFilter + ':' + btn.dataset.cmValue, btn.dataset.cmLabel || btn.dataset.cmValue);
    });

    this.cacheGroups();
    this.computeCounts();

    this.links.forEach((link) => link.addEventListener('click', this.onLinkClick.bind(this)));
    if (this.navNext) this.navNext.addEventListener('click', () => this.scrollNav(1));
    if (this.navPrev) this.navPrev.addEventListener('click', () => this.scrollNav(-1));
    if (this.navScroll) {
      this._onNavScroll = () => this.updateNavArrow();
      this.navScroll.addEventListener('scroll', this._onNavScroll, { passive: true });
      window.addEventListener('resize', this._onNavScroll);
    }

    // Re-equalise card box heights on resize (columns/wrapping change) and once
    // the webfont loads (title line-heights shift).
    this._onResize = () => {
      window.clearTimeout(this._eqTimer);
      this._eqTimer = window.setTimeout(() => {
        this.equalizeBoxes();
        this.updateScrollOffset();
      }, 150);
    };
    window.addEventListener('resize', this._onResize);
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        this.equalizeBoxes();
        this.updateScrollOffset();
      });
    }

    this.setupSpy();
    this.setupFilters();
    this.setupShowMore();
    this.setupVariants();
    this.setupStickyOrder();

    // Best-effort now (layout may still shift as images/fonts finish loading —
    // the fonts.ready/load/resize hooks above correct it afterwards).
    this.updateScrollOffset();
    if (document.readyState === 'complete') {
      this.updateScrollOffset();
    } else {
      this._onWindowLoad = () => this.updateScrollOffset();
      window.addEventListener('load', this._onWindowLoad, { once: true });
    }

    // Initial paint: JS owns card visibility from here on, so drop the
    // server-rendered overflow class and render the default (unfiltered) state
    // without animating (cards are already in place).
    this.groups.forEach((g) => g._cm.cards.forEach((c) => c.classList.remove('cm-card--overflow')));
    this.applyFilters(false);
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    if (this.stickyOrderObserver) this.stickyOrderObserver.disconnect();
    if (this._onNavScroll) window.removeEventListener('resize', this._onNavScroll);
    if (this._onResize) window.removeEventListener('resize', this._onResize);
    if (this.mq && this._onMq) this.mq.removeEventListener('change', this._onMq);
    if (this._onWindowLoad) window.removeEventListener('load', this._onWindowLoad);
  }

  // Anchor/scroll-spy jumps to a category need to clear the sticky header AND
  // this section's own sticky category nav — both heights vary per viewport
  // (announcement bar on/off, mobile vs desktop nav layout, font-driven
  // reflow), so measure the real rendered heights instead of a guessed
  // constant. Exposed as --cm-scroll-offset, read by .collection-menu__group's
  // scroll-margin-top in custom.css.
  updateScrollOffset() {
    const headerHeight = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0;
    const navHeight = this.nav ? this.nav.getBoundingClientRect().height : 0;
    const buffer = 36;
    this.style.setProperty('--cm-scroll-offset', `${headerHeight + navHeight + buffer}px`);
  }

  // Hide the fixed order button once the real page footer scrolls into view,
  // so it never sits on top of footer links/content.
  setupStickyOrder() {
    const sticky = this.querySelector('[data-cm-sticky-order]');
    const footer = document.querySelector('.custom-footer');
    if (!sticky || !footer) return;

    this.stickyOrderObserver = new IntersectionObserver(
      (entries) => {
        sticky.classList.toggle('collection-menu__sticky-order--hidden', entries[0].isIntersecting);
      },
      { rootMargin: '0px 0px -10% 0px' }
    );
    this.stickyOrderObserver.observe(footer);
  }

  // Cache per-group references + parse each card's filter handles once.
  cacheGroups() {
    this.groups.forEach((group) => {
      const cards = Array.from(group.querySelectorAll('[data-cm-card]'));
      cards.forEach((card) => {
        card._prefs = (card.dataset.cmPrefs || '').split(' ').filter(Boolean);
        card._prot = (card.dataset.cmProteines || '').split(' ').filter(Boolean);
      });
      group._cm = {
        cards,
        countEl: group.querySelector('[data-cm-count]'),
        wrap: group.querySelector('.collection-menu__show-more-wrap'),
        initial: parseInt(group.dataset.cmInitial, 10) || cards.length,
        initialMobile: parseInt(group.dataset.cmInitialMobile, 10) || cards.length,
      };
    });
  }

  // The initial "shown" count depends on viewport (fewer on mobile).
  initialFor(meta) {
    return this.mq && this.mq.matches ? meta.initialMobile : meta.initial;
  }

  get anyActive() {
    return this.selected['preferences-alimentaires'].size > 0 || this.selected.proteines.size > 0;
  }

  // Does a card satisfy the selection of the given group? (no selection = yes)
  // Dietary preferences are constraints → AND (the card must carry EVERY
  // selected preference: Végétarien + Faible en gluten = vegetarian low-gluten
  // products only). Proteins are choices → OR (Saumon + Thon = either).
  passesGroup(card, type) {
    const set = this.selected[type];
    if (!set || set.size === 0) return true;
    if (type === 'proteines') {
      return card._prot.some((h) => set.has(h));
    }
    for (const h of set) {
      if (!card._prefs.includes(h)) return false;
    }
    return true;
  }

  // Faceted, live counts. Each option's "(N)" is how many cards would match if
  // that option were ADDED to the current selection.
  // - Preferences are AND, so a preference count also respects the other
  //   selected preferences (Végétarien selected → "Faible en gluten (N)" = the
  //   vegetarian low-gluten products). Options that would narrow to 0 dim out.
  // - Proteins are OR, so protein counts stay independent of the other selected
  //   proteins but respect the preference constraint.
  // Counts card occurrences so the number matches what actually shows.
  // Recomputed on every filter change.
  computeCounts() {
    const counts = {};
    const add = (key) => {
      counts[key] = (counts[key] || 0) + 1;
    };
    this.groups.forEach((group) => {
      group._cm.cards.forEach((card) => {
        if (this.passesGroup(card, 'proteines') && this.passesGroup(card, 'preferences-alimentaires')) {
          card._prefs.forEach((h) => add('preferences-alimentaires:' + h));
        }
        if (this.passesGroup(card, 'preferences-alimentaires')) {
          card._prot.forEach((h) => add('proteines:' + h));
        }
      });
    });
    this.querySelectorAll('[data-cm-count-for]').forEach((span) => {
      const n = counts[span.dataset.cmCountFor] || 0;
      span.textContent = ' (' + n + ')';
      const btn = span.closest('[data-cm-filter]');
      // Dim options that would yield nothing (unless already selected).
      if (btn) btn.classList.toggle('is-empty', n === 0 && btn.getAttribute('aria-pressed') !== 'true');
    });
  }

  // --- Navigation -----------------------------------------------------------
  get scrollOffset() {
    const navH = this.nav ? this.nav.getBoundingClientRect().height : 0;
    return navH + 24;
  }

  onLinkClick(event) {
    const id = event.currentTarget.dataset.cmNavLink;
    const target = document.getElementById(id);
    if (!target) return;
    event.preventDefault();
    const top = target.getBoundingClientRect().top + window.pageYOffset - this.scrollOffset;
    window.scrollTo({ top, behavior: 'smooth' });
    this.setActive(id);
    history.replaceState(null, '', '#' + id);
  }

  setActive(id) {
    this.links.forEach((l) => l.classList.toggle('is-active', l.dataset.cmNavLink === id));
    this.scrollActiveLinkIntoView(this.linkById.get(id));
  }

  scrollActiveLinkIntoView(link) {
    if (!link || !this.navScroll) return;
    const navRect = this.navScroll.getBoundingClientRect();
    const linkRect = link.getBoundingClientRect();
    if (linkRect.right > navRect.right) {
      this.navScroll.scrollBy({ left: linkRect.right - navRect.right + 24, behavior: 'smooth' });
    } else if (linkRect.left < navRect.left) {
      this.navScroll.scrollBy({ left: linkRect.left - navRect.left - 24, behavior: 'smooth' });
    }
  }

  setupSpy() {
    if (!this.groups.length || !('IntersectionObserver' in window)) return;
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) this.setActive(entry.target.id);
        });
      },
      { rootMargin: '-20% 0px -70% 0px', threshold: 0 }
    );
    this.groups.forEach((g) => this.observer.observe(g));
  }

  scrollNav(direction = 1) {
    if (!this.navScroll) return;
    this.navScroll.scrollBy({ left: direction * this.navScroll.clientWidth * 0.6, behavior: 'smooth' });
  }

  updateNavArrow() {
    if (!this.navScroll) return;
    const maxScroll = this.navScroll.scrollWidth - this.navScroll.clientWidth - 2;
    const overflows = this.navScroll.scrollWidth > this.navScroll.clientWidth + 2;
    const atEnd = this.navScroll.scrollLeft >= maxScroll;
    const atStart = this.navScroll.scrollLeft <= 2;
    if (this.navNext) this.navNext.hidden = !overflows || atEnd;
    if (this.navPrev) this.navPrev.hidden = !overflows || atStart;
  }

  // Equalise the white card box heights per grid row so the "Ingrédients"
  // toggles that sit BELOW the boxes line up — independently of title/variant
  // line-count AND of whether any accordion is open (the box height is fixed
  // here, so opening a toggle only adds the panel below without resizing boxes).
  equalizeBoxes() {
    this.querySelectorAll('[data-cm-grid]').forEach((grid) => {
      const boxes = Array.from(grid.children)
        .filter(
          (card) =>
            card.classList.contains('cm-card') &&
            !card.classList.contains('cm-card--empty') &&
            card.style.display !== 'none'
        )
        .map((card) => card.querySelector('.cm-card__box'))
        .filter(Boolean);

      // Reset, then measure natural heights.
      boxes.forEach((box) => {
        box.style.minHeight = '';
      });
      if (boxes.length < 2) return;

      // Group boxes by their card's row (offsetTop), then match each row's tallest.
      const rows = new Map();
      boxes.forEach((box) => {
        const top = Math.round(box.parentElement.offsetTop);
        if (!rows.has(top)) rows.set(top, []);
        rows.get(top).push(box);
      });
      rows.forEach((rowBoxes) => {
        let max = 0;
        rowBoxes.forEach((box) => {
          max = Math.max(max, box.offsetHeight);
        });
        rowBoxes.forEach((box) => {
          box.style.minHeight = max + 'px';
        });
      });
    });
  }

  // --- Filtering ------------------------------------------------------------
  setupFilters() {
    this.querySelectorAll('[data-cm-filter]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const pressed = btn.getAttribute('aria-pressed') === 'true';
        this.setFilter(btn.dataset.cmFilter, btn.dataset.cmValue, !pressed);
        this.closeMobileFilters();
      });
    });

    const clearBtn = this.querySelector('[data-cm-clear]');
    if (clearBtn) clearBtn.addEventListener('click', () => this.clearAll());

    // Remove a single filter by clicking its chip (event delegation).
    if (this.chipsList) {
      this.chipsList.addEventListener('click', (e) => {
        const chip = e.target.closest('[data-cm-chip]');
        if (chip) this.setFilter(chip.dataset.cmFilter, chip.dataset.cmValue, false);
      });
    }

    // Mobile-only collapse toggle for the filter sidebar.
    this.filtersAside = this.querySelector('[data-cm-filters]');
    this.filtersToggle = this.querySelector('[data-cm-filters-toggle]');
    if (this.filtersAside && this.filtersToggle) {
      this.filtersToggle.addEventListener('click', () => {
        const open = this.filtersAside.classList.toggle('is-open');
        this.filtersToggle.setAttribute('aria-expanded', String(open));
      });
    }
  }

  // Collapse the mobile filter panel (no-op visually on desktop).
  closeMobileFilters() {
    if (this.filtersAside) this.filtersAside.classList.remove('is-open');
    if (this.filtersToggle) this.filtersToggle.setAttribute('aria-expanded', 'false');
  }

  // Single entry point for changing a filter. The selection Set is the single
  // source of truth; every button's pressed state is derived from it in
  // syncFilterButtons(), so the sidebar pills and chips can never drift apart.
  setFilter(type, value, on) {
    const set = this.selected[type];
    if (!set) return;
    if (on) set.add(value);
    else set.delete(value);
    this.applyFilters();
  }

  clearAll() {
    Object.values(this.selected).forEach((s) => s.clear());
    this.applyFilters();
  }

  // Reflect the selection Set onto every filter button's aria-pressed.
  syncFilterButtons() {
    this.querySelectorAll('[data-cm-filter]').forEach((btn) => {
      const set = this.selected[btn.dataset.cmFilter];
      const on = !!(set && set.has(btn.dataset.cmValue));
      btn.setAttribute('aria-pressed', String(on));
    });
  }

  // Rebuild the active-filter chips from the current selection.
  renderChips() {
    if (!this.activeBar || !this.chipsList) return;
    const active = this.anyActive;
    this.activeBar.hidden = !active;
    this.chipsList.textContent = '';
    if (!active) return;

    Object.entries(this.selected).forEach(([type, set]) => {
      set.forEach((value) => {
        const label = this.labelMap.get(type + ':' + value) || value;
        const li = document.createElement('li');
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'collection-menu__chip';
        btn.dataset.cmChip = '';
        btn.dataset.cmFilter = type;
        btn.dataset.cmValue = value;
        btn.setAttribute('aria-label', 'Retirer ' + label);
        btn.innerHTML =
          '<span>' +
          label +
          '</span><svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false"><path d="M6 6L18 18M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';
        li.appendChild(btn);
        this.chipsList.appendChild(li);
      });
    });
  }

  cardMatches(card) {
    return (
      this.passesGroup(card, 'preferences-alimentaires') && this.passesGroup(card, 'proteines')
    );
  }

  // Crossfade the results while the grid is reflowed, so cards/groups appearing
  // and disappearing don't snap. The DOM mutation happens while faded out.
  applyFilters(animate = true) {
    const apply = () => {
      const active = this.anyActive;
      this.syncFilterButtons();
      this.groups.forEach((group) => this.renderGroup(group, active));
      this.updateNavVisibility();
      this.renderChips();
      this.computeCounts();
      this.equalizeBoxes();
    };

    if (!animate) {
      apply();
      return;
    }

    // User-driven change: bring the section back into view.
    this.scrollToTop();

    if (this.reduceMotion || !this.results) {
      apply();
      return;
    }

    this.results.classList.add('is-cm-updating');
    window.clearTimeout(this._fadeTimer);
    this._fadeTimer = window.setTimeout(() => {
      apply();
      requestAnimationFrame(() => this.results.classList.remove('is-cm-updating'));
    }, 170);
  }

  // Smooth-scroll up to the top of the section (only if it's above the
  // viewport top, so it never yanks the page downward).
  scrollToTop() {
    const top = this.getBoundingClientRect().top + window.pageYOffset;
    if (window.pageYOffset <= top + 1) return;
    window.scrollTo({ top, behavior: this.reduceMotion ? 'auto' : 'smooth' });
  }

  renderGroup(group, active) {
    const meta = group._cm;

    // A collection with no products is never shown (heading, placeholder, nav).
    if (meta.cards.length === 0) {
      group.style.display = 'none';
      group._visible = false;
      return;
    }

    if (active) {
      let matched = 0;
      meta.cards.forEach((card) => {
        const ok = this.cardMatches(card);
        card.style.display = ok ? '' : 'none';
        if (ok) matched += 1;
      });
      group.style.display = matched ? '' : 'none';
      group._visible = matched > 0;
      if (meta.wrap) meta.wrap.style.display = 'none';
      if (meta.countEl) meta.countEl.textContent = (meta.countEl.dataset.cmCountTemplate || '([count])').replace('[count]', matched);
      return;
    }

    // Default (unfiltered): honour the initial limit + "Voir plus".
    const expanded = group.dataset.cmExpanded === 'true';
    const initial = this.initialFor(meta);
    meta.cards.forEach((card, i) => {
      card.style.display = expanded || i < initial ? '' : 'none';
    });
    group.style.display = '';
    group._visible = true;
    if (meta.wrap) meta.wrap.style.display = !expanded && meta.cards.length > initial ? '' : 'none';
    if (meta.countEl) {
      meta.countEl.textContent = (meta.countEl.dataset.cmCountTemplate || '([count])').replace('[count]', meta.countEl.dataset.cmTotal || meta.cards.length);
    }
  }

  updateNavVisibility() {
    this.links.forEach((link) => {
      const group = document.getElementById(link.dataset.cmNavLink);
      const item = link.closest('.collection-menu__nav-item');
      if (item) item.style.display = group && group._visible === false ? 'none' : '';
    });
    this.updateNavArrow();
  }

  // "Voir plus": reveal the cards held back by the initial limit, each with a
  // small fade/slide-in, then hide the button.
  setupShowMore() {
    this.querySelectorAll('[data-cm-show-more]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const group = btn.closest('[data-cm-group]');
        if (!group) return;
        const meta = group._cm;
        const initial = this.initialFor(meta);
        group.dataset.cmExpanded = 'true';
        btn.setAttribute('aria-expanded', 'true');

        meta.cards.forEach((card, i) => {
          if (i < initial || card.style.display !== 'none') return;
          card.style.display = '';
          if (this.reduceMotion) return;
          card.classList.add('is-cm-enter');
          requestAnimationFrame(() =>
            requestAnimationFrame(() => card.classList.remove('is-cm-enter'))
          );
        });

        if (meta.wrap) meta.wrap.style.display = 'none';
        this.equalizeBoxes();
      });
    });
  }

  setupVariants() {
    // Delegated so it covers filtered-in and "Voir plus"-revealed cards alike.
    // Radio-style: the current variant's chip is server-rendered active; there
    // is always exactly one active chip, and re-clicking it is a no-op.
    this.addEventListener('click', (event) => {
      const btn = event.target.closest('[data-cm-variant]');
      if (!btn || btn.classList.contains('is-active')) return;
      const card = btn.closest('[data-cm-card]');
      if (!card) return;

      card.querySelectorAll('[data-cm-variant]').forEach((b) => {
        const on = b === btn;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });

      // Per-variant ingredients: swap the accordion body to this variant's
      // text; hide the whole accordion when the variant has none.
      const ing = card.querySelector('[data-cm-ing]');
      if (ing) {
        const body = ing.querySelector('[data-cm-ing-body]');
        const text = btn.dataset.cmVariantIng || '';
        if (text) {
          if (body) body.textContent = text;
          ing.hidden = false;
        } else {
          ing.open = false;
          ing.hidden = true;
        }
      }

      const img = card.querySelector('img.cm-card__image');
      if (!img) return;
      if (!('cmBaseSrc' in img.dataset)) {
        img.dataset.cmBaseSrc = img.getAttribute('src') || '';
        img.dataset.cmBaseSrcset = img.getAttribute('srcset') || '';
        img.dataset.cmBaseAlt = img.getAttribute('alt') || '';
      }

      if (btn.dataset.cmVariantImg) {
        img.src = btn.dataset.cmVariantImg;
        if (btn.dataset.cmVariantSrcset) img.setAttribute('srcset', btn.dataset.cmVariantSrcset);
        else img.removeAttribute('srcset');
        img.alt = img.dataset.cmBaseAlt + ' — ' + btn.textContent.trim();
      } else {
        // Variant has no image of its own — fall back to the product image.
        img.src = img.dataset.cmBaseSrc;
        if (img.dataset.cmBaseSrcset) img.setAttribute('srcset', img.dataset.cmBaseSrcset);
        else img.removeAttribute('srcset');
        img.alt = img.dataset.cmBaseAlt;
      }
    });
  }
}

if (!customElements.get('collection-menu')) {
  customElements.define('collection-menu', CollectionMenu);
}
